/* eslint-disable no-nested-ternary */
import React from "react";

import {
  Button,
  Checkbox,
  InputNumber,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import PropTypes from "prop-types";

import TableUserInfoItem from "../../Components/TableUserInfoItem/TableUserInfoItem";
import { useAppTheme } from "../../context/ThemeProvider";
import {
  DATE_FORMAT,
  TIER_COLOR_MAP,
  WL_PLAN_COLOR_MAP,
} from "../../Utils/constants";
import { numFormatter } from "../../Utils/formatter";
import {
  COLUMN_FEATURES,
  FEATURE_LABELS,
  formatAiCredits,
  getFeatureColumnLabel,
  getFeatureColumnWidth,
  getMangoAiMonthlyAvailable,
  isAiVerseEnabled,
  MANAGEABLE_FEATURES,
  MANGO_AI_CREDIT_FIELD,
  MANGO_AI_FEATURES,
  PLAN_TYPE_OPTIONS,
  UNFILTERABLE_FEATURES,
  USER_TIER_OPTIONS,
} from "./manageFeaturesUtils";

const UsersTable = ({
  data,
  loading,
  pagination,
  onTableChange,
  selectedRowKeys,
  onSelectChange,
  onFilterChange,
  filters,
  setFilters,
}) => {
  const { COLORS } = useAppTheme();

  const handleFilterChange = (filterKey, value) => {
    if (onFilterChange) {
      onFilterChange(filterKey, value);
    }
  };

  // const getFilterDropdown = (type, options, filterKey) => ({
  //   filterDropdown: ({
  //     setSelectedKeys,
  //     selectedKeys,
  //     confirm,
  //     clearFilters,
  //   }) => (
  //     <div style={{ padding: 8 }}>
  //       {type === "search" ? (
  //         <Input
  //           placeholder="Search..."
  //           value={selectedKeys[0]}
  //           onChange={(e) =>
  //             setSelectedKeys(e.target.value ? [e.target.value] : [])
  //           }
  //           onPressEnter={() => confirm()}
  //           style={{ marginBottom: 8, display: "block" }}
  //         />
  //       ) : (
  //         <Select
  //           mode={type === "multiple" ? "multiple" : undefined}
  //           placeholder={`Select ${type}...`}
  //           value={type === "multiple" ? selectedKeys : selectedKeys[0]}
  //           onChange={(value) => {
  //             let keys;
  //             if (type === "multiple") {
  //               keys = value;
  //             } else {
  //               keys = value ? [value] : [];
  //             }
  //             setSelectedKeys(keys);
  //           }}
  //           style={{ minWidth: 200, marginBottom: 8, display: "block" }}
  //         >
  //           {options?.map((option) => (
  //             <Select.Option key={option.value} value={option.value}>
  //               {option.label}
  //             </Select.Option>
  //           ))}
  //         </Select>
  //       )}

  //       <div
  //         style={{
  //           width: "100%",
  //           display: "flex",
  //           justifyContent: "space-between",
  //           gap: 8,
  //         }}
  //       >
  //         <Button
  //           onClick={() => {
  //             clearFilters();
  //             setTimeout(() => {
  //               handleFilterChange(filterKey, null);
  //             }, 100);
  //           }}
  //           size="small"
  //           block
  //         >
  //           Reset
  //         </Button>
  //         <Button
  //           type="primary"
  //           onClick={() => {
  //             const value =
  //               type === "multiple" ? selectedKeys : selectedKeys[0];
  //             confirm();
  //             setTimeout(() => {
  //               handleFilterChange(filterKey, value);
  //             }, 100);
  //           }}
  //           size="small"
  //           block
  //         >
  //           Done
  //         </Button>
  //       </div>
  //     </div>
  //   ),
  // });

  const getFeatureToggleFilter = (filterKey) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      const isChecked = selectedKeys[0] === "enabled";

      return (
        <div style={{ padding: 8, width: 200 }}>
          <div style={{ marginBottom: 4 }}>
            <Typography.Text
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: COLORS.TEXT_TITLE,
              }}
            >
              Filter Options
            </Typography.Text>
          </div>

          <Checkbox
            checked={isChecked}
            onChange={(e) => {
              const value = e.target.checked ? "enabled" : null;
              setSelectedKeys(value ? [value] : []);
            }}
            style={{ marginBottom: 8 }}
          >
            <Typography.Text style={{ fontSize: 13 }}>
              Show only enabled
            </Typography.Text>
          </Checkbox>

          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <Button
              onClick={() => {
                clearFilters();
                setTimeout(() => {
                  handleFilterChange(filterKey, null);
                }, 100);
              }}
              size="small"
              block
            >
              Reset
            </Button>
            <Button
              type="primary"
              onClick={() => {
                const value = selectedKeys[0] || null;
                confirm();
                setTimeout(() => {
                  handleFilterChange(filterKey, value);
                }, 100);
              }}
              size="small"
              block
            >
              Done
            </Button>
          </div>
        </div>
      );
    },
    filteredValue: filters[filterKey] === "enabled" ? ["enabled"] : null,
  });

  const getStorageLimitFilterDropdown = () => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      const minValue = selectedKeys[0];
      const maxValue = selectedKeys[1];

      return (
        <div style={{ padding: 8, width: 220 }}>
          <div style={{ marginBottom: 8 }}>
            <Typography.Text
              style={{ fontSize: 12, color: COLORS.TEXT_SUBTITLE }}
            >
              Storage Range (GB)
            </Typography.Text>
          </div>

          <Space.Compact style={{ width: "100%", marginBottom: 8 }}>
            <InputNumber
              placeholder="Min"
              value={minValue}
              onChange={(value) => setSelectedKeys([value, maxValue])}
              min={0}
              max={maxValue || undefined}
              style={{ width: "50%" }}
              size="small"
            />

            <InputNumber
              placeholder="Max"
              value={maxValue}
              onChange={(value) => setSelectedKeys([minValue, value])}
              min={minValue || 0}
              style={{ width: "50%" }}
              size="small"
            />
          </Space.Compact>

          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <Button
              onClick={() => {
                clearFilters();
                setTimeout(() => {
                  setFilters({
                    ...filters,
                    STORAGE_LIMIT_FROM: null,
                    STORAGE_LIMIT_TO: null,
                    // Reset user tier while clearing storage range filter as only tier1 has limited storage
                    ...(filters?.userTier === "tier1" && {
                      userTier: null,
                    }),
                  });
                }, 100);
              }}
              size="small"
              block
            >
              Reset
            </Button>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setTimeout(() => {
                  if (minValue && maxValue) {
                    setFilters({
                      ...filters,
                      // Other tiers have unlimited storage, only tier1 has limited storage
                      userTier: "tier1",
                      STORAGE_LIMIT_FROM: minValue,
                      STORAGE_LIMIT_TO: maxValue,
                    });
                  }
                }, 100);
              }}
              size="small"
              block
            >
              Done
            </Button>
          </div>
        </div>
      );
    },
    filteredValue:
      filters.STORAGE_LIMIT_FROM || filters.STORAGE_LIMIT_TO
        ? [filters.STORAGE_LIMIT_FROM, filters.STORAGE_LIMIT_TO].filter(Boolean)
        : null,
  });

  const renderExpiryDate = (expiryAt) => {
    if (!expiryAt) return null;

    const expiryMoment = moment(expiryAt);
    const now = moment();
    const isExpired = expiryMoment.isBefore(now);

    let relativeText;
    let color;

    if (isExpired) {
      color = COLORS.ERROR;
      relativeText = `Expired ${expiryMoment.fromNow()}`;
    } else {
      const daysLeft = expiryMoment.diff(now, "days") + 1;
      color = daysLeft <= 7 ? "#fa8c16" : COLORS.TEXT_SUBTITLE;

      if (daysLeft === 1) {
        const hoursLeft = expiryMoment.diff(now, "hours");
        relativeText =
          hoursLeft > 0
            ? `Expires in ${hoursLeft} ${hoursLeft === 1 ? "hour" : "hours"}`
            : "Expires soon";
      } else if (daysLeft <= 30) {
        relativeText = `Expires in ${daysLeft} days`;
      } else {
        const months = expiryMoment.diff(now, "months");
        if (months < 12) {
          relativeText = `Expires in ${months} ${
            months === 1 ? "month" : "months"
          }`;
        } else {
          const years = expiryMoment.diff(now, "years");
          relativeText = `Expires in ${years} ${
            years === 1 ? "year" : "years"
          }`;
        }
      }
    }

    return (
      <div style={{ whiteSpace: "nowrap", lineHeight: 1.4 }}>
        <Typography.Text style={{ fontSize: 11, color }}>
          <ClockCircleOutlined style={{ marginRight: 3 }} />
          {expiryMoment.format(DATE_FORMAT)}
        </Typography.Text>
        <br />
        <Typography.Text strong style={{ fontSize: 11, color }}>
          {relativeText}
        </Typography.Text>
      </div>
    );
  };

  // The seven AI features share one column, with the AI Verse add-on on top and
  // the credit balance underneath. They are separate features now, but the
  // screen still reads as one group.
  // Compact expiry for the AI feature rows: a date far enough out reads better
  // as the date itself, while anything inside 30 days is more useful as the
  // time remaining, since that is when it needs attention.
  // Compact expiry shown under its feature: a date far enough out reads better
  // as the date itself, while anything inside 30 days is more useful as the
  // time remaining, since that is when it needs attention.
  const renderInlineExpiry = (expiryAt) => {
    if (!expiryAt) return null;

    const expiryMoment = moment(expiryAt);
    const now = moment();
    const daysLeft = expiryMoment.diff(now, "days");
    const hoursLeft = expiryMoment.diff(now, "hours");

    let text;
    let color = COLORS.TEXT_SUBTITLE;

    if (expiryMoment.isBefore(now)) {
      text = `Expired ${expiryMoment.fromNow()}`;
      color = COLORS.ERROR;
    } else if (daysLeft > 30) {
      text = expiryMoment.format(DATE_FORMAT);
    } else {
      color = "#fa8c16";
      if (daysLeft >= 1) {
        text = `Expires in ${daysLeft} ${daysLeft === 1 ? "day" : "days"}`;
      } else if (hoursLeft >= 1) {
        text = `Expires in ${hoursLeft} ${hoursLeft === 1 ? "hour" : "hours"}`;
      } else {
        text = "Expires very soon";
      }
    }

    return (
      <Typography.Text
        style={{
          fontSize: 11,
          color,
          display: "block",
          whiteSpace: "nowrap",
          marginTop: 2,
        }}
      >
        <ClockCircleOutlined style={{ marginRight: 3 }} />
        {text}
      </Typography.Text>
    );
  };

  const renderMangoAiColumn = (record) => {
    const mangoAiConfig = record.featuresConfig?.[MANAGEABLE_FEATURES.MANGO_AI];
    const granted = MANGO_AI_FEATURES.filter(
      (feature) => record.featuresConfig?.[feature]?.enabled
    );
    const credits = mangoAiConfig?.[MANGO_AI_CREDIT_FIELD];
    const monthlyAvailable = getMangoAiMonthlyAvailable(mangoAiConfig);

    return (
      <Space direction="vertical" size={4}>
        <Tag color={isAiVerseEnabled(record) ? "gold" : "default"}>
          AI Verse: {isAiVerseEnabled(record) ? "On" : "Off"}
        </Tag>

        {granted.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {granted.map((feature) => (
              <div key={feature}>
                <Tag color="success" style={{ marginRight: 0 }}>
                  {FEATURE_LABELS[feature]}
                </Tag>
                {renderInlineExpiry(record.featuresConfig?.[feature]?.expiryAt)}
              </div>
            ))}
          </div>
        ) : (
          <Tag color="error">Disabled</Tag>
        )}

        <Typography.Text style={{ fontSize: 11, color: COLORS.TEXT_SUBTITLE }}>
          <strong style={{ fontWeight: 500 }}>Credits: </strong>
          {formatAiCredits(credits) ?? "NOT SET"}
        </Typography.Text>

        {monthlyAvailable !== null && (
          <Typography.Text
            style={{ fontSize: 11, color: COLORS.TEXT_SUBTITLE }}
          >
            <strong style={{ fontWeight: 500 }}>Monthly available: </strong>
            {formatAiCredits(monthlyAvailable)}
          </Typography.Text>
        )}
      </Space>
    );
  };

  const renderFeatureColumn = (feature, record) => {
    if (feature === MANAGEABLE_FEATURES.MANGO_AI) {
      return renderMangoAiColumn(record);
    }

    const featureConfig = record.featuresConfig?.[feature];

    if (!featureConfig) {
      return <Tag color="error">Disabled</Tag>;
    }

    const { enabled, expiryAt, storage } = featureConfig;

    return (
      <Space direction="vertical" size={2}>
        {feature === MANAGEABLE_FEATURES.STORAGE_LIMIT ? (
          (() => {
            // Show unlimited for Advanced and Ultimate plans
            if (
              record.userTier &&
              ["tier2", "tier3", "tier4"].includes(record.userTier)
            ) {
              return (
                <Typography.Text
                  style={{ color: COLORS.SUCCESS, fontWeight: 500 }}
                >
                  Unlimited
                </Typography.Text>
              );
            }

            if (typeof storage !== "number") {
              return (
                <Typography.Text
                  type="secondary"
                  strong
                  style={{ fontSize: 12 }}
                >
                  NOT SET
                </Typography.Text>
              );
            }

            return <Typography.Text>{storage} GB</Typography.Text>;
          })()
        ) : (
          <>
            {/* {platform && (
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, color: COLORS.TEXT_SUBTITLE }}
              >
                <strong style={{ fontWeight: 500 }}>Platform: </strong>{" "}
                {PLATFORM_TITLE[platform]}
              </Typography.Text>
            )} */}

            {enabled ? (
              <Tag color="success">Enabled</Tag>
            ) : (
              <Tag color="error">Disabled</Tag>
            )}
            {/* {enabled === undefined && (
              <Typography.Text type="secondary" strong style={{ fontSize: 12 }}>
                NOT SET
              </Typography.Text>
            )} */}

            {expiryAt ? renderExpiryDate(expiryAt) : null}
          </>
        )}
      </Space>
    );
  };

  // What each feature's cell body needs; the shared helper then widens it if
  // the column title would not fit on one line.
  const featureContentWidth = (feature) => {
    if (feature === MANAGEABLE_FEATURES.STORAGE_LIMIT) return 160;
    if (feature === MANAGEABLE_FEATURES.MANGO_AI) return 230;
    return 120;
  };

  const renderUserTier = (userTier) => {
    const tierOption = USER_TIER_OPTIONS.find(
      (option) => option.value === userTier
    );
    return tierOption ? (
      <Tag color={TIER_COLOR_MAP[userTier]}>{tierOption.label}</Tag>
    ) : (
      <Typography.Text type="secondary" strong style={{ fontSize: 12 }}>
        NOT SET
      </Typography.Text>
    );
  };

  const renderPlanType = (planType) => {
    const planOption = PLAN_TYPE_OPTIONS.find(
      (option) => option.value === planType
    );
    return planOption ? (
      <Tag color={WL_PLAN_COLOR_MAP[planType]}>{planOption.label}</Tag>
    ) : (
      <Typography.Text type="secondary" strong style={{ fontSize: 12 }}>
        NOT SET
      </Typography.Text>
    );
  };

  const columns = [
    {
      title: "Sl. No.",
      key: "serialNumber",
      width: 80,
      fixed: "left",
      render: (_, record, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return (
          <Typography.Text strong style={{ fontSize: 14 }}>
            {numFormatter({
              amount: serialNumber,
              hideCurrency: true,
              hideTooltip: true,
            })}
          </Typography.Text>
        );
      },
    },
    {
      title: "User Info",
      dataIndex: "name",
      key: "userInfo",
      width: 330,
      render: (_, record) => (
        <TableUserInfoItem
          name={record?.name}
          profilePic={record?.profilePicUrl}
          email={record?.email}
          phone={record?.phone}
          host={record?.host}
          isCopyable
        />
      ),
    },
    {
      title: "User Tier",
      dataIndex: "userTier",
      key: "userTier",
      width: 120,
      render: renderUserTier,
      filters: USER_TIER_OPTIONS.map((option) => ({
        text: option.label,
        value: option.value,
      })),
      filterMultiple: false,
      onFilter: (value, record) => record.userTier === value,
      filteredValue: filters.userTier ? [filters.userTier] : null,
    },
    {
      title: "Plan Type",
      dataIndex: "whitelabelPlanType",
      key: "planType",
      width: 150,
      render: renderPlanType,
      filters: PLAN_TYPE_OPTIONS.map((option) => ({
        text: option.label,
        value: option.value,
      })),
      filterMultiple: false,
      onFilter: (value, record) => record.whitelabelPlanType === value,
      filteredValue: filters.planType ? [filters.planType] : null,
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (date) => moment(date).format(DATE_FORMAT),
    },
    {
      title: (
        <Tooltip title="Hours an auto-approved standard withdrawal waits before payout. Hosts without a value use the tier default.">
          <div>Withdrawal Delay</div>
        </Tooltip>
      ),
      dataIndex: "standardWithdrawalDelay",
      key: "standardWithdrawalDelay",
      width: 180,
      render: (value) =>
        typeof value === "number" ? (
          <Typography.Text>
            {value === 0
              ? "No delay"
              : `${value} ${value === 1 ? "hr" : "hrs"}`}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary" strong style={{ fontSize: 12 }}>
            TIER DEFAULT
          </Typography.Text>
        ),
    },
    ...COLUMN_FEATURES.map((feature) => ({
      title: (
        <Tooltip title={`${getFeatureColumnLabel(feature)} status`}>
          <div>{getFeatureColumnLabel(feature)}</div>
        </Tooltip>
      ),
      dataIndex: ["featuresConfig", feature],
      key: feature,
      width: getFeatureColumnWidth(feature, featureContentWidth(feature)),
      render: (_, record) => renderFeatureColumn(feature, record),
      // the backend ignores ?MANGO_AI=true, so chat access carries no filter
      // even though the other AI features do
      ...(UNFILTERABLE_FEATURES.includes(feature)
        ? {}
        : getFeatureToggleFilter(feature)),
      ...(feature === MANAGEABLE_FEATURES.STORAGE_LIMIT
        ? getStorageLimitFilterDropdown()
        : {}),
    })),
  ];

  // antd only honours column widths literally when scroll.x is a number. With
  // `x: "auto"` the inner table renders at `width: auto; min-width: 100%`, so
  // table-layout:fixed sizes it to the container and shares the slack across
  // the columns — every width becomes a hint, which is what squeezed the AI
  // Features column until its dates wrapped a character per line. The checkbox
  // column is added by rowSelection rather than declared in `columns`, so it
  // needs its own allowance in the total.
  const SELECTION_COLUMN_WIDTH = 60;
  const tableScrollX = columns.reduce(
    (total, column) => total + (column.width || 150),
    SELECTION_COLUMN_WIDTH
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: false,
      name: record.name,
    }),
  };

  return (
    <div className="manage-features-users-table">
      <Table
        loading={loading}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            total > 1 ? `${range[0]}-${range[1]} of ${total} users` : undefined,
          pageSizeOptions: [10, 20, 25, 30, 50, 100, 1000],
        }}
        scroll={{ x: tableScrollX }}
        onChange={onTableChange}
        rowKey={(record) => record._id}
        locale={{
          emptyText: loading ? "Loading..." : "No users found",
        }}
        style={{
          backgroundColor: COLORS.SURFACE,
          borderColor: COLORS.BORDER,
        }}
      />
    </div>
  );
};

UsersTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool.isRequired,
  pagination: PropTypes.shape({
    current: PropTypes.number,
    pageSize: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  filters: PropTypes.shape({
    userTier: PropTypes.string,
    planType: PropTypes.string,
    ADD_CUSTOMERS: PropTypes.string,
    CHECKOUT_TIMER: PropTypes.string,
    PREMIUM_TEMPLATES: PropTypes.string,
    SPARKS: PropTypes.string,
    STORAGE_LIMIT_FROM: PropTypes.number,
    STORAGE_LIMIT_TO: PropTypes.number,
  }),
  onTableChange: PropTypes.func.isRequired,
  selectedRowKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
};

export default UsersTable;
